/**
 * NFL roster slots — the league's own, not a constant.
 *
 * ── WHAT THIS REPLACES ──────────────────────────────────────────────────────
 *
 * Five surfaces each held their own hardcoded 21-slot list (V2 RosterRail, the legacy
 * draft roster panel, My Team, Matchup, Roster Settings). Every one of them was wrong for
 * any customised league, in both directions at once:
 *
 *   - it rendered IDP1/IDP2 rows to leagues with no IDP slot, which can never fill;
 *   - no list contained FLEX2, so a player the server correctly assigned to FLEX2 had NO
 *     ROW TO APPEAR IN and vanished from their own roster view;
 *   - the header counted against 21 rather than the league's real slot count.
 *
 * League of Trash (18 slots, two FLEX, no IDP, no TE) exhibits all three.
 *
 * ── THE RULE, MIRRORED FROM THE SERVER ──────────────────────────────────────
 *
 * SAB-951 made `nfl_acquire_pick_lock` derive slots from `settings.roster_slots` by
 * matching `^<base>[0-9]*$`, with the 21-slot array as the fallback for a league that has
 * none. This is the display half of the same rule and MUST agree with it: the writer
 * decides which slot a player occupies, and these surfaces decide whether that slot has a
 * row. If they disagree, the disagreement is invisible — a real player silently missing
 * from a real roster, which is exactly the FLEX2 bug above.
 *
 * `DEFAULT_NFL_ROSTER_SLOTS` is byte-identical to the server's fallback array and to the
 * `roster_slots` column default. It is the correct answer for the 7 prod leagues whose
 * settings were wiped (SAB-953), not a guess.
 */

/**
 * The fallback slot template. Identical to the server's fallback in
 * `nfl_acquire_pick_lock` and to `_shared/nfl-slot-eligibility.ts`'s DEFAULT_SLOT_TEMPLATE.
 *
 * ── WHY IR IS IN THE LIST ───────────────────────────────────────────────────
 *
 * Prod holds rows at IR1/IR2. Omitting those slots would make those players vanish from
 * their own manager's roster view while still occupying a roster spot.
 */
export const DEFAULT_NFL_ROSTER_SLOTS: readonly string[] = [
  'QB', 'RB1', 'RB2', 'WR1', 'WR2', 'TE', 'FLEX',
  'K', 'P', 'DST', 'COACH', 'IDP1', 'IDP2',
  'BN1', 'BN2', 'BN3', 'BN4', 'BN5', 'BN6',
  'IR1', 'IR2',
];

/**
 * Kept as the DEFAULT, not as "the" slot order.
 *
 * Retained under its old name so nothing that legitimately wants the default (tests, a
 * league with no settings) has to change, but every rendering surface now calls
 * `leagueSlotOrder` instead. A bare import of this in a component is now a bug.
 */
export const NFL_ROSTER_SLOT_ORDER = DEFAULT_NFL_ROSTER_SLOTS as string[];

/**
 * This league's slots, in the commissioner's declared order.
 *
 * Accepts the raw `settings.roster_slots` value — unknown on purpose, because it arrives
 * from a jsonb column via `select('*')` and nothing upstream validates it. A malformed or
 * empty array falls back rather than rendering a roster with no rows at all, matching the
 * server's `loadLeagueSlots` decision exactly.
 */
export function leagueSlotOrder(rosterSlots: unknown): string[] {
  if (!Array.isArray(rosterSlots) || rosterSlots.length === 0) {
    return [...DEFAULT_NFL_ROSTER_SLOTS];
  }
  const slots = rosterSlots.filter((s): s is string => typeof s === 'string' && s.length > 0);
  return slots.length > 0 ? slots : [...DEFAULT_NFL_ROSTER_SLOTS];
}

/** `RB1` -> `RB`, `FLEX2` -> `FLEX`, `QB` -> `QB`. The `^<base>[0-9]*$` rule, inverted. */
export function slotBase(slot: string): string {
  return slot.replace(/[0-9]+$/, '');
}

export function isBenchSlot(slot: string): boolean {
  return /^BN[0-9]*$/.test(slot);
}

export function isIrSlot(slot: string): boolean {
  return /^IR[0-9]*$/.test(slot);
}

/**
 * Starters are everything that is neither bench nor IR — defined by exclusion, exactly as
 * the server defines "draftable slots". Defining starters by an allowlist of known names
 * is what made FLEX2 unreachable: a slot nobody had thought of became invisible rather
 * than merely unstyled.
 */
/**
 * The slots a DRAFT can fill: this league's slots minus injured reserve.
 *
 * SAB-986: the draft-room roster headers counted against the league's FULL slot list, so
 * League of Trash (18 slots, one IR) read "15 / 18" mid-draft and every team finished
 * "17 / 18" with IR1 empty forever. Nothing was wrong with the roster -- the denominator
 * was simply counting a slot the draft cannot reach. A progress counter that can never
 * reach its own total is worse than no counter.
 *
 * IR is not draftable by design: 204 picks over 12 teams is 17 each, which is exactly the
 * 17 non-IR slots. Players arrive on IR later, through the lineup path.
 *
 * SCOPE -- CORRECTED 2026-09-02 (SAB-986 reopened). This paragraph previously said the
 * rule was draft-only, and that post-draft views "legitimately show total capacity
 * including IR ... Do not reach for this helper there." That was my scoping call, taken
 * from the original ticket, and Adam overruled it after the My Team header shipped
 * reading "17/18":
 *
 *   IR is a GHOST SLOT -- somewhere to store an injured player, not active roster
 *   capacity. It never counts toward an x/N capacity figure, on any surface.
 *
 * So this is the capacity rule everywhere, not the draft rule. The name is narrower than
 * the concept for want of a rename; read it as "slots that count as capacity".
 *
 * What IR still gets is its OWN section with its own count (My Team's IR block, Settings'
 * Injured Reserve card). Excluding it from the total is not the same as hiding it, and a
 * player parked there must stay visible -- that was the SAB-886 disappearance.
 *
 * Takes an already-expanded slot list rather than raw settings, because both callers have
 * already resolved theirs via `leagueSlotOrder` and must not resolve it twice.
 */
export function draftableSlots(slotOrder: readonly string[]): string[] {
  return slotOrder.filter(s => !isIrSlot(s));
}

export function isStarterSlot(slot: string): boolean {
  return !isBenchSlot(slot) && !isIrSlot(slot);
}

/**
 * Every slot in this league whose base is `slotBase` — the `^<base>[0-9]*$` match, and the
 * direct mirror of `slotsForBase` in `_shared/nfl-slot-eligibility.ts`.
 *
 * Returns them in the league's declared order. An empty result is meaningful and must not
 * be treated as "not configured": it means this league has no starting slot of that base
 * at all, which is the true state of IDP in a league with `IDP: 0`.
 *
 * Bench and IR are excluded, matching the server, so this answers "where can this START",
 * never "where can this sit".
 */
export function slotsForBase(base: string, rosterSlots: unknown): string[] {
  const re = new RegExp(`^${base}[0-9]*$`);
  return leagueSlotOrder(rosterSlots).filter((s) => isStarterSlot(s) && re.test(s));
}

export interface PartitionedSlots {
  starters: string[];
  bench: string[];
  ir: string[];
  /** Everything the league declares, in order. Bench and IR included. */
  all: string[];
}

/** Split a league's slots into the three groups the roster surfaces render separately. */
export function partitionSlots(rosterSlots: unknown): PartitionedSlots {
  const all = leagueSlotOrder(rosterSlots);
  return {
    all,
    starters: all.filter(isStarterSlot),
    bench: all.filter(isBenchSlot),
    ir: all.filter(isIrSlot),
  };
}

/**
 * Short display label for a slot.
 *
 * Derived from the base rather than looked up, so a slot the product has never shipped
 * before (FLEX3, IDP3) still renders a sensible label instead of falling through to blank.
 * The two irregular ones are spelled out; everything else is its own base.
 */
const IRREGULAR_LABELS: Record<string, string> = {
  FLEX: 'FLX',
  COACH: 'HC',
};

export function slotLabel(slot: string): string {
  const base = slotBase(slot);
  return IRREGULAR_LABELS[base] ?? base;
}

/**
 * SAB-1016: is this roster full, for the purposes of "does Add need a drop"?
 *
 * ── WHY THIS EXISTS RATHER THAN `roster.length >= 21` ───────────────────────
 *
 * Both NFL players surfaces asked that question wrongly. The desktop modal hardcoded 21 in
 * two places, which is the DEFAULT template's size and wrong for any league that customised
 * roster_slots -- League of Trash has 18. And 21 counted IR players in the numerator while
 * the drop list filtered them out of the denominator, so a team with a player on IR was told
 * it was full when it was not, and then offered no one to drop. Mobile had no capacity
 * notion at all and simply let the server 409.
 *
 * Capacity is the league's own DRAFTABLE slots -- SAB-986: "IR is a GHOST SLOT ... It never
 * counts toward an x/N capacity figure, on any surface." Occupancy must exclude IR for the
 * same reason, or the two halves disagree and the bug comes back inverted.
 */
export function isRosterFull(
  rosterSlots: unknown,
  activeSlotsOccupied: readonly string[],
): boolean {
  const capacity = draftableSlots(leagueSlotOrder(rosterSlots)).length;
  const occupied = activeSlotsOccupied.filter(s => !isIrSlot(s)).length;
  return occupied >= capacity;
}
